
  <template>
    <div
      class="py-4 px-4 h-100"
    >
      <template>
        <section>
          <b-row
            class="mb-3"
          >
            <b-col cols="auto">
              <base-help-tooltip
                popover-placement="bottom"
              >
                <template v-slot:left>
                  <h1>
                    Geo Performance
                  </h1>
                </template>
                <template v-slot:popover-body>
                  <p
                    class="mb-0"
                  >
                    A breakdown of how the campaign has delivered and performed across different geographic locations.
                  </p>
                </template>
              </base-help-tooltip>
            </b-col>
          </b-row>
        </section>
        <section>
          <Filters
            tab="Geo Performance"
            @updateReport="updateReport"
            @setPageLoading="setPageLoading"
            @updateDates="updateDates"
            @areAllParametersPresent="areAllParametersPresent"
          />
        </section>
        <div
          v-if="isPageLoading"
          class="loader mx-auto h-100"
        />
        <div v-else>
          <section>
            <b-row>
              <b-col
                cols="4"
                class="h-50"
              >
                <b-card
                >
                  <h3>Impressions by Country</h3>
                  <PieChart
                    title="Impressions by Country"
                    :data="getGeoLocationStats('impressions')"
                    :background-colours="['#29345C', '#294599', '#2A51C7', '#5473D1', '#B8C2FD', '#1D5468', '#0D8EB4', '#02BAED', '#34C7F1', '#7EDAF6', '#C8EDFB']"
                    v-if="showGeoReports"
                  />
                  <div v-else class="d-flex align-items-center justify-content-center h-100">
                    <b-alert
                      show
                      variant="warning"
                      class="w-50 mx-auto text-center"
                    >
                      There's no data to show
                    </b-alert>
                  </div>
                </b-card>
              </b-col>
              <b-col
                cols="4"
                class="h-50"
              >
                <b-card>
                  <h3>CTR by Country</h3>
                  <PieChart
                    title="CTR by Country"
                    :data="getGeoLocationStats('ctr')"
                    :background-colours="['#BAB3F5', '#9488EF', '#7969EB', '#D7B3F5', '#C188EF', '#8957B3', '#B269EB', '#FDBDF4', '#FE98ED', '#BF66B1', '#FF81E8']"
                    type="percentage"
                    v-if="showGeoReports"
                  />
                  <div v-else class="d-flex align-items-center justify-content-center h-100">
                    <b-alert
                      show
                      variant="warning"
                      class="w-50 mx-auto text-center"
                    >
                      There's no data to show
                    </b-alert>
                  </div>
                </b-card>
              </b-col>
              <b-col
                cols="4"
                class="h-50"
              >
                <b-card
                  class="h-50"
                >
                  <h3>Cost by Country</h3>
                  <PieChart
                    title="Cost by Country"
                    :legend-is-clickable="false"
                    :data="getGeoLocationStats('cost')"
                    :background-colours="['#2A604A', '#2DAC6F', '#30E48A', '#81DEB1', '#B9EAD2', '#444E37', '#6E8B4B', '#92D04A', '#93E436', '#BAE48C', '#E6F8D2']"
                    type="percentage"
                    v-if="showGeoReports"
                  />
                  <div v-else class="d-flex align-items-center justify-content-center h-100">
                    <b-alert
                      show
                      variant="warning"
                      class="w-50 mx-auto text-center"
                    >
                      There's no data to show
                    </b-alert>
                  </div>
                </b-card>
              </b-col>
            </b-row>
            <b-row class="h-25">
              <b-col>
                <b-card>
                  <b-table
                    class="card-table table-vcenter text-nowrap"
                    sticky-header
                    :striped="true"
                    :bordered="false"
                    :hover="true"
                    :fields="tableFields"
                    :items="geoStats"
                    sort-by="name"
                    v-if="showGeoReports"
                  >
                    <template v-slot:cell(geoLocationId)="data">
                      {{ getGeoLocationName(data.item.geoLocationId) }}
                    </template>
                    <template v-slot:cell(impressions)="data">
                      {{ data.item.impressions.toLocaleString() }}
                    </template>
                    <template v-slot:cell(clicks)="data">
                      {{ data.item.clicks.toLocaleString() }}
                    </template>
                    <template v-slot:cell(ctr)="data">
                      {{ data.item.ctr.toFixed(2) }}%
                    </template>
                    <template v-slot:cell(str)="data">
                      {{ data.item.str.toFixed(2) }}%
                    </template>
                    <template v-slot:cell(cost)="data">
                      {{ data.item.cost.toFixed(2) }}%
                    </template>
                  </b-table>
                  <b-alert
                    v-else
                    show
                    variant="warning"
                    class="w-50 mx-auto text-center"
                  >
                    There's no data to show
                  </b-alert>
                </b-card>
              </b-col>
            </b-row>
          </section>
        </div>
      </template>
    </div>
  </template>
  <script>
  import { mapState, mapGetters, mapActions } from 'vuex';
  import * as reportingTypes from '@/modules/reporting/store/types';
  import * as campaignTypes from '@/modules/campaigns/store/types';

  import helper from '@/core/helper';
  import Filters from '@/modules/reporting/components/Filters.vue';
  import PieChart from '@/modules/reporting/components/PieChart.vue';
  import numbersHelper from '@/core/mixins/numbersHelper';
  import fileHelper from '@/core/mixins/fileHelper';
  import BaseHelpTooltip from '@/components/BaseHelpTooltip.vue';

  export default {
    name: 'GeoPerformance',
    components: {
      Filters,
      BaseHelpTooltip,
      PieChart,
    },
    mixins: [
      numbersHelper,
      fileHelper,
    ],
    data() {
      return {
        isPageLoading: true,
        selectedCampaignIds: [],
        selectedPublishersIds: [],
        selectedCreativesIds: [],
        selectedContentItemIds: [],
        areAllGeosSelected: [],
        selectedGeoIds: [],
        dateRange: {
          start: -1,
          end: -1,
        },
        geoStats: [],
        allParametersArePresent: false,
        scrollableCreatives: false,
      };
    },
    computed: {
      ...mapState('campaigns', [
        'geoLocations',
      ]),
      ...mapGetters('campaigns', [
        campaignTypes.GET_ORDERS_START_DATE,
        campaignTypes.GET_ORDERS_END_DATE,
      ]),
      startDateForReport() {
        return this.dateRange?.start == null || this.dateRange.start === -1
          ? this.GET_ORDERS_START_DATE : this.dateRange.start;
      },
      endDateForReport() {
        return this.dateRange?.end == null || this.dateRange.end === -1
          ? this.GET_ORDERS_END_DATE : this.dateRange.end;
      },
      tableFields() {
        return [
          {
            key: 'name',
            label: 'Country',
            sortable: true,
            thClass: 'text-left',
            tdClass: 'text-left',
          },
          {
            key: 'impressions',
            label: 'Impressions',
            sortable: true,
            thClass: 'text-left',
            tdClass: 'text-left',
          },
          {
            key: 'clicks',
            label: 'Clicks',
            sortable: true,
            thClass: 'text-left',
            tdClass: 'text-left',
          },
          {
            key: 'ctr',
            label: 'CTR %',
            sortable: true,
            thClass: 'text-left',
            tdClass: 'text-left',
          },
          ...(this.scrollableCreatives ? [
            { key: 'str',
              label: 'STR %',
              sortable: true,
              thClass: 'text-left',
              tdClass: 'text-left', }
          ] : []),
          {
            key: 'cost',
            label: 'Cost',
            sortable: true,
            thClass: 'text-left',
            tdClass: 'text-left',
          }
        ];
      },
      showGeoReports() {
        return this.allParametersArePresent && this.selectedGeoIds.length > 0 && this.geoStats.length > 0;
      },
    },
    methods: {
      ...mapActions('reporting', [
        reportingTypes.FETCH_GEO_STATS,
      ]),
      updateDates(startDate, endDate) {
        this.dateRange.start = startDate;
        this.dateRange.end = endDate;
        this.updateReport();
      },
      updateReport: helper.debounce(function fetchData(data) {
        if (this.allParametersArePresent) {
          this.selectedCampaignIds = data.campaignIds;
          this.selectedPublishersIds = data.publisherIds;
          this.selectedCreativesIds = data.creativeIds;
          this.selectedContentItemIds = data.contentItemIds;
          this.areAllGeosSelected = data.areAllGeosSelected;
          this.selectedGeoIds = data.geoIds;
          this.fetchGeoData();
        }
      }, 1500),
      fetchGeoData() {
        this.setPageLoading(true);
        const payload = {
          campaignIds: this.selectedCampaignIds,
          body: {
            fromDate: this.startDateForReport,
            toDate: this.endDateForReport,
            publisherIds: this.selectedPublishersIds,
            creativeIds: this.selectedCreativesIds,
            contentItemIds: this.selectedContentItemIds,
            geoLocationIds: this.areAllGeosSelected ? [] : this.selectedGeoIds,
          },
        };

        this.FETCH_GEO_STATS(payload)
          .then((response) => {
            if (response.stats) {

              const allHaveStr = response.stats.every(item => item.str > 0.00);
              this.scrollableCreatives = allHaveStr;

              this.geoStats = response.stats.map((item) => ({
                name: this.getGeoLocationName(item.geoLocationId),
                impressions: item.impressions,
                clicks: item.clicks,
                ctr: item.ctr,
                str: allHaveStr ? item.str : null,
                cost: item.spendPercentage,

              }));
            }
            this.setPageLoading(false);
          });
      },
      setPageLoading(value) {
        this.isPageLoading = value;
      },
      areAllParametersPresent(value) {
        this.allParametersArePresent = value;
      },
      getGeoLocationName(id) {
        if (!id || this.geoLocations.length === 0) return 'Unknown';
        const geoLocation = this.geoLocations.find((location) => location.id === id);
        return geoLocation ? geoLocation.name : 'Unknown';
      },
      getGeoLocationStats(sortField, limit = 10) {
        const sortedData = [...this.geoStats].sort((a, b) => b[sortField] - a[sortField]);
        const topN = sortedData.slice(0, limit);
        const rest = sortedData.slice(limit);

        if (rest.length > 0) {
          let otherValue;

          if (sortField === 'ctr') {
            const totalImpressions = rest.reduce((sum, item) => sum + (item.impressions || 0), 0);
            const totalClicks = rest.reduce((sum, item) => sum + (item.clicks || 0), 0);
            otherValue = totalImpressions > 0 ? parseFloat(((totalClicks / totalImpressions) * 100).toFixed(2)) : 0;
          } else {
            otherValue = rest.reduce((sum, item) => sum + item[sortField], 0);
          }

          topN.push({ name: 'Other', [sortField]: otherValue });
        }

        const labels = topN.map((item) => item.name);
        const values = topN.map((item) => item[sortField]);

        return { labels, values };
      },
    },
  };
  </script>
  <style scoped lang="scss">
  $not-first-column-width: 10%;
  ::v-deep {
    .card-table thead tr th:not(:first-child),
    .card-table tbody tr td:not(:first-child) {
      width: var(--not-first-column-width, $not-first-column-width);
    }
    .table.b-table > thead > tr > [aria-sort] {
      background-position: right 0.5rem center;
    }
    .card-table thead tr th:not(:last-child),
    .card-table tbody tr td:not(:last-child) {
      border-right: 1px solid rgb(222, 226, 230);
    }
  }
  </style>
